/**
 * Deep links — the URLs the native shell will route into the app.
 *
 * **A deep link navigates and prefills. It never writes.** Any app on the
 * phone, and any web page, can open `tidetally://…`; a link that saved an
 * expense would be cross-site request forgery with a ledger as its target.
 * Writing without the numpad is reserved for what the OS mediates — App
 * Intents the user invoked, widget and tile buttons (native sub-plan §1).
 *
 * So this returns a route and a query string for the screen to *read*, and
 * nothing else. Only the following navigation shapes are allowed:
 *
 * - `tidetally://capture?category=<id>&amount=<integer cents>` → the numpad,
 *   prefilled. The user still taps Save.
 * - `https://tally.leeseidon.com/settings/agents?code=<device code>` → the
 *   pairing screen, with the code typed in for them.
 *
 * - `tidetally://plan` → subscription settings from a trial reminder.
 *
 * Everything else is null. A link carrying a parameter we know in a shape we do
 * not accept is refused whole rather than half-applied; a parameter we do not
 * know is dropped, so a newer link still opens an older app.
 */

import { FAMILY_LINK_TOKEN_MAX_LENGTH } from "../../shared/family_limits";
import { MAX_AMOUNT_CENTS } from "@/domain/money";

export interface DeepLinkRoute {
  readonly path: string;
  /** `""` or `?…`, with only the allowed parameters, in a fixed order. */
  readonly search: string;
}

const APP_SCHEME = "tidetally:";
const APP_PREFIX = `${APP_SCHEME}//`;
/** An app link's host: lowercase letters, then a path, query, fragment or nothing. */
const APP_HOST = /^[a-z]+(?=[/?#]|$)/;
const CAPTURE_HOST = "capture";
const WEB_ORIGIN_HOST = "tally.leeseidon.com";
const AGENTS_PATH = "/settings/agents";

/** Integer cents: digits only — no sign, no decimal point, no exponent. */
const CENTS = /^\d{1,9}$/;
/** A category id: a UUID in practice; never anything that could be a path. */
const CATEGORY_ID = /^[A-Za-z0-9-]{1,64}$/;
/** `TIDE-` and four of the device flow's alphabet (`server/oauth_device.ts`). */
const DEVICE_CODE = /^TIDE-[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{4}$/;

/**
 * Reads `tidetally://host/path?query` as if it were `https://`.
 *
 * The URL parser's rules for a non-special scheme are not the same in every
 * WebView: before Chromium 130, `tidetally://capture?amount=1` parses with an
 * empty hostname and a pathname of `//capture`, so every capture link was
 * refused on phones with an older WebView. Swapping in a special scheme gives
 * the same host, path and query everywhere; the checks after it are unchanged.
 * Null when `raw` is not an app link or does not parse.
 */
function appUrl(raw: string): URL | null {
  if (raw.slice(0, APP_PREFIX.length).toLowerCase() !== APP_PREFIX) return null;
  const rest = raw.slice(APP_PREFIX.length);
  // The https parser is more forgiving than the scheme ever was: it drops extra
  // slashes, reads `\` as `/`, decodes %-escapes and lowercases the host. A
  // host of plain lowercase letters keeps every link that was refused refused.
  if (!APP_HOST.test(rest)) return null;
  try {
    return new URL(`https://${rest}`);
  } catch {
    // Not a URL at all — refused, and the caller reports it (INV-7).
    return null;
  }
}

function single(params: URLSearchParams, key: string): string | null | undefined {
  const values = params.getAll(key);
  if (values.length === 0) return null;
  if (values.length > 1) return undefined;
  return values[0];
}

function buildSearch(entries: [string, string][]): string {
  return entries.length === 0 ? "" : `?${new URLSearchParams(entries).toString()}`;
}

function parseCapture(url: URL): DeepLinkRoute | null {
  if (url.pathname !== "" && url.pathname !== "/") return null;
  const entries: [string, string][] = [];

  const category = single(url.searchParams, "category");
  if (category === undefined) return null;
  if (category !== null) {
    if (!CATEGORY_ID.test(category)) return null;
    entries.push(["category", category]);
  }

  const amount = single(url.searchParams, "amount");
  if (amount === undefined) return null;
  if (amount !== null) {
    if (!CENTS.test(amount) || Number(amount) > MAX_AMOUNT_CENTS) return null;
    entries.push(["amount", String(Number(amount))]);
  }
  return { path: "/", search: buildSearch(entries) };
}

function parseAgents(url: URL): DeepLinkRoute | null {
  if (url.pathname !== AGENTS_PATH) return null;
  const code = single(url.searchParams, "code");
  if (code === undefined) return null;
  if (code === null) return { path: AGENTS_PATH, search: "" };
  if (!DEVICE_CODE.test(code)) return null;
  return { path: AGENTS_PATH, search: buildSearch([["code", code]]) };
}

export function parseDeepLink(raw: string): DeepLinkRoute | null {
  const app = appUrl(raw);
  if (app !== null) {
    if (app.username !== "" || app.password !== "" || app.port !== "") return null;
    if (app.hostname === "plan" && (app.pathname === "" || app.pathname === "/") && app.search === "" && app.hash === "") return { path: "/settings/plan", search: "" };
    if (app.hostname === CAPTURE_HOST) return parseCapture(app);
    return null;
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    // Not a URL at all. `null` is this function's answer for every link it will
    // not route — reported to the caller, not swallowed (INV-7).
    return null;
  }
  if (url.username !== "" || url.password !== "" || url.port !== "") return null;

  if (url.protocol === "https:" && url.hostname === WEB_ORIGIN_HOST) {
    if (new RegExp(`^/join/(?:s/)?[A-Za-z0-9_-]{1,${FAMILY_LINK_TOKEN_MAX_LENGTH}}$`).test(url.pathname)) return { path: url.pathname, search: "" };
    return parseAgents(url);
  }
  return null;
}

/** What the numpad may be prefilled with. Never saved: the user still taps Save. */
export interface CapturePrefill {
  readonly categoryId: string | null;
  readonly amountCents: number | null;
}

/**
 * The prefill carried by `/`'s query string — from a deep link, an icon
 * shortcut, a widget on iOS 16, or anyone typing the URL. Re-validated here
 * rather than trusted because the route is reachable without `parseDeepLink`
 * (a browser can open `/?amount=…` directly). Null when there is nothing to
 * prefill, or when a known parameter is malformed — refused whole, exactly as
 * the link would be.
 */
export function capturePrefill(search: string): CapturePrefill | null {
  const url = appUrl(`${APP_PREFIX}${CAPTURE_HOST}${search}`);
  const route = url === null ? null : parseCapture(url);
  if (route === null || route.search === "") return null;
  const params = new URLSearchParams(route.search);
  const amount = params.get("amount");
  return {
    categoryId: params.get("category"),
    amountCents: amount === null ? null : Number(amount),
  };
}
